<!-- AppointmentCalendar.vue -->
<script setup>
import { computed, onMounted, ref } from 'vue'
import { Calendar as CalendarIcon, Clock } from 'lucide-vue-next'
import { useMeetingStore } from '@/store/meetingStore'
import { formatDayDate, formatTime } from '@/lib/utils/dateUtils'

// Store integration
const meetingStore = useMeetingStore()

// Calendar state
const currentDate = ref(new Date())
const selectedDate = ref(null)
const showAppointments = ref(false)

// Get current month and year
const currentMonth = computed(() => currentDate.value.getMonth())
const currentYear = computed(() => currentDate.value.getFullYear())

// Format the month name
const monthName = computed(() => {
  return new Intl.DateTimeFormat('fr-FR', { month: 'long' }).format(
    currentDate.value,
  )
})

// Days of week headers (using French locale)
const daysOfWeek = ['D', 'L', 'M', 'M', 'J', 'V', 'S']

// Generate dates for current month view
const calendarDays = computed(() => {
  const firstDay = new Date(currentYear.value, currentMonth.value, 1)
  const lastDay = new Date(currentYear.value, currentMonth.value + 1, 0)

  // Get the number of days in the month
  const daysInMonth = lastDay.getDate()

  // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = firstDay.getDay()

  // Get the last day of the previous month
  const prevMonthLastDay = new Date(
    currentYear.value,
    currentMonth.value,
    0,
  ).getDate()

  // Calendar array with 6 weeks (42 days)
  const days = []

  // Add days from previous month
  for (let i = 0; i < firstDayOfWeek; i++) {
    days.push({
      day: prevMonthLastDay - firstDayOfWeek + i + 1,
      month: currentMonth.value - 1,
      year: currentYear.value,
      isCurrentMonth: false,
    })
  }

  // Add days from current month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      day: i,
      month: currentMonth.value,
      year: currentYear.value,
      isCurrentMonth: true,
    })
  }

  // Add days from next month
  const remainingDays = 42 - days.length
  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      day: i,
      month: currentMonth.value + 1,
      year: currentYear.value,
      isCurrentMonth: false,
    })
  }

  return days
})

// Get all meetings from the store
const allMeetings = computed(() => {
  return meetingStore.meets.filter(meet => meet.status === 'scheduled')
})

// Get meetings for a specific day
function getMeetingsForDay(day) {
  const dateObj = new Date(day.year, day.month, day.day)
  const dateStr = formatDayDate(dateObj)

  return allMeetings.value.filter((meeting) => {
    const meetingDate = formatDayDate(new Date(meeting.startTime))
    return meetingDate === dateStr
  })
}

// Check if a day has meetings
function hasMeetings(day) {
  return getMeetingsForDay(day).length > 0
}

// Get meetings for selected date
const selectedDateMeetings = computed(() => {
  if (!selectedDate.value)
    return []
  return getMeetingsForDay(selectedDate.value).sort((a, b) => {
    return new Date(a.startTime) - new Date(b.startTime)
  })
})

// Format duration as string
function formatDuration(minutes) {
  if (minutes < 60) {
    return `${minutes} minutes`
  }
  else {
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60

    if (remainingMinutes === 0) {
      return hours === 1 ? '1 heure' : `${hours} heures`
    }
    else {
      return `${hours}h${remainingMinutes}`
    }
  }
}

// Determine the meeting type based on title or other properties
function getMeetingType(meeting) {
  const titleLower = meeting.title?.toLowerCase() || ''

  if (titleLower.includes('premier') || titleLower.includes('initial')) {
    return 'first'
  }
  else if (titleLower.includes('thérapie') || titleLower.includes('suivi')) {
    return 'therapy'
  }
  else if (titleLower.includes('mental') || titleLower.includes('consult')) {
    return 'consultation'
  }

  return 'consultation'
}

// Navigate to previous month
function previousMonth() {
  currentDate.value = new Date(currentYear.value, currentMonth.value - 1, 1)
}

// Navigate to next month
function nextMonth() {
  currentDate.value = new Date(currentYear.value, currentMonth.value + 1, 1)
}

// Select a date
function selectDate(day) {
  if (hasMeetings(day)) {
    selectedDate.value = day
    showAppointments.value = true
  }
  else {
    selectedDate.value = day
    showAppointments.value = false
  }
}

// Check if date is today
function isDateToday(day) {
  const today = new Date()
  return (
    day.day === today.getDate()
    && day.month === today.getMonth()
    && day.year === today.getFullYear()
  )
}

// Data is loaded globally in App.vue, no need to fetch here
onMounted(() => {
  // Data will be available from App.vue's global loading
  console.log('DashboardCalendar mounted - using global store data')
})

// Get patient info from store
function getPatient(patientId) {
  // Check store
  const patientFromStore = meetingStore.patients.find(
    p => p.id === patientId,
  )
  if (patientFromStore)
    return patientFromStore

  // Default
  return {
    name: 'Patient inconnu',
  }
}
</script>

<template>
  <div>
    <!-- Calendar header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center">
        <CalendarIcon class="w-5 h-5 text-gray-600 mr-2" />
        <h2 class="text-xl font-semibold text-gray-800 capitalize">
          {{ monthName }} {{ currentYear }}
        </h2>
      </div>
      <div class="flex items-center space-x-2">
        <button
          class="p-2 rounded-full hover:bg-gray-100"
          @click="previousMonth"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
        <button class="p-2 rounded-full hover:bg-gray-100" @click="nextMonth">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Calendar grid -->
    <div class="grid grid-cols-7 gap-2">
      <!-- Day names -->
      <div
        v-for="(day, index) in daysOfWeek"
        :key="index"
        class="text-center text-sm font-medium text-gray-500 py-2"
      >
        {{ day }}
      </div>

      <!-- Calendar days -->
      <div
        v-for="day in calendarDays"
        :key="`${day.year}-${day.month}-${day.day}`"
        class="aspect-square p-1 relative cursor-pointer"
        @click="selectDate(day)"
      >
        <div
          class="w-full h-full flex items-center justify-center rounded-full relative"
          :class="{
            'text-gray-400': !day.isCurrentMonth,
            'bg-purple-600 text-white':
              selectedDate
              && selectedDate.day === day.day
              && selectedDate.month === day.month
              && selectedDate.year === day.year,
            'hover:bg-gray-100': !(
              selectedDate
              && selectedDate.day === day.day
              && selectedDate.month === day.month
              && selectedDate.year === day.year
            ),
            'bg-purple-100':
              isDateToday(day)
              && !(
                selectedDate
                && selectedDate.day === day.day
                && selectedDate.month === day.month
                && selectedDate.year === day.year
              ),
          }"
        >
          {{ day.day }}
          <!-- Red dot indicator for days with appointments -->
          <div
            v-if="hasMeetings(day)"
            class="absolute bottom-1 w-1.5 h-1.5 bg-red-500 rounded-full"
          />
        </div>
      </div>
    </div>

    <!-- Appointments list for selected date -->
    <div
      v-if="showAppointments && selectedDateMeetings.length > 0"
      class="mt-7"
    >
      <!--
        <h3 class="font-semibold text-gray-700 mb-2">
        Rendez-vous du
        {{
          new Date(
            selectedDate.year,
            selectedDate.month,
            selectedDate.day
          ).toLocaleDateString("fr-FR")
        }}
      </h3> -->

      <router-link
        v-for="meeting in selectedDateMeetings"
        :key="meeting.id"
        :to="`/meets/${meeting.id}`"
        class="mt-3 border-l-4 rounded-lg p-3 flex items-start gap-3 bg-purple-100 text-purple-700"
        :class="{
          'border-blue-400': getMeetingType(meeting) === 'first',
          'border-green-400': getMeetingType(meeting) === 'therapy',
          'border-purple-400': getMeetingType(meeting) === 'consultation',
        }"
      >
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <Clock class="h-4 w-4 text-gray-500" />
            <span class="text-sm font-medium">
              {{ formatTime(meeting.startTime) }} à
              {{
                formatTime(
                  new Date(
                    new Date(meeting.startTime).getTime()
                      + meeting.duration * 60000,
                  ),
                )
              }}
            </span>
          </div>
          <h4 class="font-medium mt-1">
            {{ getPatient(meeting.patientId).name }}
          </h4>
          <p class="text-sm text-gray-600 mt-1">
            {{ meeting.title }}
          </p>
        </div>
        <div
          class="px-2 py-1 text-xs font-medium rounded-full"
          :class="{
            'bg-blue-100 text-blue-700': getMeetingType(meeting) === 'first',
            'bg-green-100 text-green-700':
              getMeetingType(meeting) === 'therapy',
            'bg-purple-100 text-purple-700':
              getMeetingType(meeting) === 'consultation',
          }"
        >
          {{ formatDuration(meeting.duration) }}
        </div>
      </router-link>
    </div>
  </div>
</template>
